import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

// Public Square config — Application ID + Location ID are safe on the client. The access token is
// NEVER here; tokenization happens against Square directly and only the one-time token is sent to
// our backend. Sandbox vs production is env-driven.
const ENV = process.env.REACT_APP_SQUARE_ENVIRONMENT || 'sandbox';
const APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
const SDK_URL = ENV === 'production'
  ? 'https://web.squarecdn.com/v1/square.js'
  : 'https://sandbox.web.squarecdn.com/v1/square.js';

function loadSquareSdk() {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const existing = document.querySelector('script[data-square-sdk]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Square));
      existing.addEventListener('error', () => reject(new Error('Failed to load Square')));
      return;
    }
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.setAttribute('data-square-sdk', '1');
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error('Failed to load Square'));
    document.head.appendChild(s);
  });
}

// Renders Square's hosted card fields and, on submit, tokenizes the card and hands the one-time
// token back to the parent via onToken(token). The parent sends that token to the backend.
export function SquareCardForm({ amountLabel, onToken }) {
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let card;
    let disposed = false;
    (async () => {
      if (!APP_ID || !LOCATION_ID) {
        setError('Card payment is not configured.');
        return;
      }
      const Square = await loadSquareSdk();
      const payments = Square.payments(APP_ID, LOCATION_ID);
      card = await payments.card();
      await card.attach('#sq-card');
      if (!disposed) {
        cardRef.current = card;
        setReady(true);
      }
    })().catch((e) => setError(e.message || 'Failed to load the card form'));
    return () => {
      disposed = true;
      try { card?.destroy?.(); } catch { /* noop */ }
    };
  }, []);

  const pay = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    setError('');
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== 'OK') {
        throw new Error(result.errors?.[0]?.message || 'Please check your card details.');
      }
      await onToken(result.token);
    } catch (e) {
      setError(e.message || 'Payment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div id="sq-card" data-testid="square-card-container" className="min-h-[90px]" />
      {error && <p className="text-sm text-destructive" data-testid="square-card-error">{error}</p>}
      <Button
        data-testid="square-pay-btn"
        disabled={!ready || busy}
        onClick={pay}
        className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover transition-colors duration-150"
      >
        {busy ? <><Loader2 size={16} className="animate-spin mr-2" /> Processing…</> : `Pay ${amountLabel}`}
      </Button>
    </div>
  );
}
